import {z} from 'zod'

import {tzDateFromIso} from 'src/api/zod-helpers'
import {equipmentBilling} from 'src/jobs/billing-types'

import {
    customerContactRefRawSchema,
    customerContactRefSchema,
    customerContactSourceSchema,
} from '../customers/schemas'
import {locationInputSchema, toLocationRequest} from '../jobs/location-input'
import {jobCommentRawSchema} from '../jobs/schemas'
import {userAccountRawSchema, userAccountSchema} from '../users/schemas'
import {mapPartialPickup, partialPickupRawSchema} from '../work/partial-pickup-schema'

const projectLifecycleStatusSchema = z.enum(['estimate', 'in_progress', 'closed'])
const projectBillingMethodSchema = z.enum([
    'line_rates',
    'hourly_transportation',
    'flat_rate',
    'per_load',
])

const jobStatusSchema = z.enum(['planned', 'active', 'completed', 'cancelled'])

const jobScheduleRoleSchema = z.enum(['deploy', 'service', 'pickup'])

const jobScheduleRawSchema = z.object({
    start_date: z.string(),
    start_time: z.string().nullable(),
    end_date: z.string().nullable(),
    end_time: z.string().nullable(),
})

const requirementFulfillmentRawSchema = z.object({
    workers_required: z.number(),
    workers_assigned: z.number(),
    equipment_required: z.number(),
    equipment_assigned: z.number(),
})

export const projectScheduleSummaryRawSchema = z.object({
    id: z.string(),
    start_date: z.string(),
    end_date: z.string(),
    service_type_name: z.string(),
    cadence_weekdays: z.array(z.number()),
    governed_job_count: z.number(),
    deploy_job_display_id: z.string(),
    po_number: z.string().nullable().optional(),
    project_billing_terms_id: z.string().optional(),
})

function mapRequirementFulfillment(raw: z.infer<typeof requirementFulfillmentRawSchema>) {
    return {
        workersRequired: raw.workers_required,
        workersAssigned: raw.workers_assigned,
        equipmentRequired: raw.equipment_required,
        equipmentAssigned: raw.equipment_assigned,
    }
}

export const projectListItemRawSchema = z.object({
    id: z.string(),
    name: z.string(),
    customer_id: z.string(),
    customer_name: z.string(),
    description: z.string(),
    created_at: z.string(),
    status: projectLifecycleStatusSchema,
    approved_to_start: z.boolean(),
    archived_at: z.string().nullable(),
    job_count: z.number(),
    planned_count: z.number(),
    active_count: z.number(),
    completed_count: z.number(),
    cancelled_count: z.number(),
    earliest_start: z.string().nullable(),
    latest_end: z.string().nullable(),
    created_by_name: z.string().nullable(),
    billing_method: projectBillingMethodSchema.nullable(),
    unbilled_total: z.string(),
    unbilled_count: z.number(),
    oldest_unbilled_at: z.string().nullable(),
    unbilled_on_draft_total: z.string(),
    billed_out_count: z.number(),
    po_number: z.string().nullable().optional(),
})

export const projectBillableLineItemRawSchema = z.object({
    id: z.string(),
    job_id: z.string().nullable(),
    charge_type: z.string(),
    name: z.string(),
    description: z.string(),
    quantity: z.string(),
    unit: z.string(),
    unit_price: z.string(),
    total_price: z.string(),
    invoice_id: z.string().nullable(),
    is_synced: z.boolean(),
    po_number: z.string().nullable().optional(),
    project_billing_terms_id: z.string().nullable().optional(),
})

const jobLineRateRawSchema = z.object({
    name: z.string(),
    quantity: z.number(),
    billing_rate: z.string().nullable(),
    billing_type: z.enum(['hourly', 'daily', 'weekly', 'monthly', 'flat', 'per_load']).nullable(),
})

export const projectJobSummaryRawSchema = jobScheduleRawSchema.extend({
    // start_date is a placeholder when date_tbc is set.
    date_tbc: z.boolean(),
    id: z.string(),
    name: z.string(),
    status: jobStatusSchema,
    display_id: z.string(),
    service_type_name: z.string(),
    schedule_role: jobScheduleRoleSchema.nullable(),
    service_schedule_id: z.string().nullable(),
    service_schedule_service_type_name: z.string().nullable(),
    po_number: z.string().nullable().optional(),
    project_billing_terms_id: z.string().optional(),
    requirement_fulfillment: requirementFulfillmentRawSchema,
    billable_line_items: z.array(projectBillableLineItemRawSchema).default([]),
    customer_contact: customerContactRefRawSchema.nullable().optional(),
    customer_contact_source: customerContactSourceSchema.nullable().optional(),
    is_pickup: z.boolean(),
    created_at: z.string().nullable(),
    partial_pickup: partialPickupRawSchema.nullable(),
    line_rates: z.array(jobLineRateRawSchema).default([]),
})

export const projectDetailRawSchema = z.object({
    id: z.string(),
    name: z.string(),
    customer_id: z.string(),
    customer_name: z.string(),
    description: z.string(),
    notes: z.string().nullable(),
    po_number: z.string().nullable().optional(),
    source_quote_id: z.string().nullable().optional(),
    internal_contact: userAccountRawSchema.nullable(),
    customer_contact: customerContactRefRawSchema.nullable().optional(),
    dispatch_yard_id: z.string().nullable(),
    created_at: z.string(),
    status: projectLifecycleStatusSchema,
    approved_to_start: z.boolean(),
    archived_at: z.string().nullable(),
    planned_count: z.number(),
    active_count: z.number(),
    jobs: z.array(projectJobSummaryRawSchema),
    schedules: z.array(projectScheduleSummaryRawSchema).default([]),
    order_number: z.string().nullable(),
    created_by_id: z.string().nullable(),
    partial_pickup: partialPickupRawSchema.nullable(),
})

export const projectListItemSchema = projectListItemRawSchema.transform((raw) => ({
    id: raw.id,
    name: raw.name,
    customerId: raw.customer_id,
    customerName: raw.customer_name,
    description: raw.description,
    createdAt: raw.created_at,
    status: raw.status,
    approvedToStart: raw.approved_to_start,
    archivedAt: raw.archived_at,
    jobCount: raw.job_count,
    plannedCount: raw.planned_count,
    activeCount: raw.active_count,
    completedCount: raw.completed_count,
    cancelledCount: raw.cancelled_count,
    earliestStart: raw.earliest_start,
    latestEnd: raw.latest_end,
    createdByName: raw.created_by_name,
    billingMethod: raw.billing_method,
    unbilledTotal: Number(raw.unbilled_total),
    unbilledCount: raw.unbilled_count,
    oldestUnbilledAt: raw.oldest_unbilled_at,
    unbilledOnDraftTotal: Number(raw.unbilled_on_draft_total),
    billedOutCount: raw.billed_out_count,
    poNumber: raw.po_number ?? null,
    projectBillingTermsId: raw.project_billing_terms_id ?? null,
}))

function mapJobSchedule(raw: z.infer<typeof jobScheduleRawSchema>) {
    return {
        startDate: raw.start_date,
        startTime: raw.start_time,
        endDate: raw.end_date,
        endTime: raw.end_time,
    }
}

export const projectJobSummarySchema = projectJobSummaryRawSchema.transform((raw) => ({
    id: raw.id,
    name: raw.name,
    status: raw.status,
    displayId: raw.display_id,
    serviceTypeName: raw.service_type_name,
    scheduleRole: raw.schedule_role,
    serviceScheduleId: raw.service_schedule_id,
    serviceScheduleServiceTypeName: raw.service_schedule_service_type_name,
    poNumber: raw.po_number ?? null,
    projectBillingTermsId: raw.project_billing_terms_id ?? null,
    requirementFulfillment: mapRequirementFulfillment(raw.requirement_fulfillment),
    ...mapJobSchedule(raw),
    dateTbc: raw.date_tbc,
    isPickup: raw.is_pickup,
    createdAt: raw.created_at ? tzDateFromIso.parse(raw.created_at) : null,
    partialPickup: mapPartialPickup(raw.partial_pickup),
    customerContact:
        raw.customer_contact == null ? null : customerContactRefSchema.parse(raw.customer_contact),
    customerContactSource: raw.customer_contact_source ?? null,
    billableLineItems: raw.billable_line_items.map((item) =>
        projectBillableLineItemSchema.parse(item),
    ),
    lineRates: raw.line_rates.map((line) => ({
        name: line.name,
        quantity: line.quantity,
        billingRate: line.billing_rate == null ? null : Number(line.billing_rate),
        billingType: line.billing_type,
    })),
}))

export const projectScheduleSummarySchema = projectScheduleSummaryRawSchema.transform((raw) => ({
    id: raw.id,
    startDate: raw.start_date,
    endDate: raw.end_date,
    serviceTypeName: raw.service_type_name,
    cadenceWeekdays: raw.cadence_weekdays,
    governedJobCount: raw.governed_job_count,
    deployJobDisplayId: raw.deploy_job_display_id,
    poNumber: raw.po_number ?? null,
    projectBillingTermsId: raw.project_billing_terms_id ?? null,
}))

export const projectDetailSchema = projectDetailRawSchema.transform((raw) => ({
    id: raw.id,
    name: raw.name,
    customerId: raw.customer_id,
    customerName: raw.customer_name,
    description: raw.description,
    notes: raw.notes,
    poNumber: raw.po_number ?? null,
    sourceQuoteId: raw.source_quote_id ?? null,
    internalContact:
        raw.internal_contact == null ? null : userAccountSchema.parse(raw.internal_contact),
    customerContact:
        raw.customer_contact == null ? null : customerContactRefSchema.parse(raw.customer_contact),
    dispatchYardId: raw.dispatch_yard_id,
    createdAt: raw.created_at,
    status: raw.status,
    approvedToStart: raw.approved_to_start,
    archivedAt: raw.archived_at,
    plannedCount: raw.planned_count,
    activeCount: raw.active_count,
    jobs: raw.jobs.map((job) => projectJobSummarySchema.parse(job)),
    schedules: raw.schedules.map((schedule) => projectScheduleSummarySchema.parse(schedule)),
    orderNumber: raw.order_number,
    createdById: raw.created_by_id,
    partialPickup: mapPartialPickup(raw.partial_pickup),
}))

export const projectBillableLineItemSchema = projectBillableLineItemRawSchema.transform((raw) => ({
    id: raw.id,
    jobId: raw.job_id,
    chargeType: raw.charge_type,
    name: raw.name,
    description: raw.description,
    quantity: Number(raw.quantity),
    unit: raw.unit,
    unitPrice: Number(raw.unit_price),
    totalPrice: Number(raw.total_price),
    invoiceId: raw.invoice_id,
    isSynced: raw.is_synced,
    poNumber: raw.po_number ?? null,
    projectBillingTermsId: raw.project_billing_terms_id ?? null,
}))

export type ProjectLifecycleStatus = z.infer<typeof projectLifecycleStatusSchema>
export type ProjectBillingMethod = z.infer<typeof projectBillingMethodSchema>
export type ProjectListItemRaw = z.infer<typeof projectListItemRawSchema>
export type ProjectListItem = z.infer<typeof projectListItemSchema>
export type ProjectDetailRaw = z.infer<typeof projectDetailRawSchema>
export type ProjectDetail = z.infer<typeof projectDetailSchema>
export type ProjectJobSummaryRaw = z.infer<typeof projectJobSummaryRawSchema>
export type ProjectJobSummary = z.infer<typeof projectJobSummarySchema>
export type ProjectScheduleSummary = z.infer<typeof projectScheduleSummarySchema>
export type ProjectScheduleSummaryRaw = z.infer<typeof projectScheduleSummaryRawSchema>
export type ProjectBillableLineItem = z.infer<typeof projectBillableLineItemSchema>
export type ProjectDisposalFeeRaw = z.infer<typeof projectDisposalFeeRawSchema>
export type ProjectDisposalFee = z.infer<typeof projectDisposalFeeSchema>

export const projectDisposalFeeRawSchema = z.object({
    id: z.string(),
    waste_type: z.string(),
    unit: z.string(),
    unit_rate: z.string(),
    estimated_quantity: z.string().nullable(),
    created_at: z.string(),
})

export const projectBillingTermsRawSchema = z.object({
    id: z.string(),
    billing_method: projectBillingMethodSchema,
    transportation_hourly_rate: z.string().nullable(),
    estimated_transportation_hours: z.string().nullable(),
    minimum_transportation_hourly_rate: z.string().nullable(),
    flat_rate_amount: z.string().nullable(),
    rate_per_load: z.string().nullable().optional(),
    per_load_notes: z.string().nullable().optional(),
    demurrage_hourly_rate: z.string().nullable(),
    disposal_fees: z.array(projectDisposalFeeRawSchema),
    washout_fee: z.string().nullable(),
    hsei_percent: z.string().nullable(),
    created_at: z.string(),
})

export const projectDisposalFeeSchema = projectDisposalFeeRawSchema.transform((raw) => ({
    id: raw.id,
    wasteType: raw.waste_type,
    unit: raw.unit,
    unitRate: Number(raw.unit_rate),
    estimatedQuantity: raw.estimated_quantity != null ? Number(raw.estimated_quantity) : null,
    createdAt: raw.created_at,
}))

export const projectBillingTermsSchema = projectBillingTermsRawSchema.transform((raw) => ({
    id: raw.id,
    billingMethod: raw.billing_method,
    transportationHourlyRate:
        raw.transportation_hourly_rate != null ? Number(raw.transportation_hourly_rate) : null,
    estimatedTransportationHours:
        raw.estimated_transportation_hours != null
            ? Number(raw.estimated_transportation_hours)
            : null,
    minimumTransportationHourlyRate:
        raw.minimum_transportation_hourly_rate != null
            ? Number(raw.minimum_transportation_hourly_rate)
            : null,
    flatRateAmount: raw.flat_rate_amount != null ? Number(raw.flat_rate_amount) : null,
    ratePerLoad: raw.rate_per_load != null ? Number(raw.rate_per_load) : null,
    perLoadNotes: raw.per_load_notes ?? null,
    demurrageHourlyRate:
        raw.demurrage_hourly_rate != null ? Number(raw.demurrage_hourly_rate) : null,
    disposalFees: raw.disposal_fees.map((fee) => projectDisposalFeeSchema.parse(fee)),
    washoutFee: raw.washout_fee != null ? Number(raw.washout_fee) : null,
    hseiPercent: raw.hsei_percent != null ? Number(raw.hsei_percent) : null,
    createdAt: raw.created_at,
}))

export type ProjectBillingTerms = z.infer<typeof projectBillingTermsSchema>

export const projectDisposalFeeInputSchema = z.object({
    wasteType: z.string(),
    unit: z.string(),
    unitRate: z.number(),
    estimatedQuantity: z.number().nullable().optional(),
})

export const createProjectBillingTermsInputSchema = z.object({
    billingMethod: projectBillingMethodSchema,
    transportationHourlyRate: z.number().nullable().optional(),
    estimatedTransportationHours: z.number().nullable().optional(),
    flatRateAmount: z.number().nullable().optional(),
    ratePerLoad: z.number().nullable().optional(),
    perLoadNotes: z.string().nullable().optional(),
    demurrageHourlyRate: z.number().nullable().optional(),
    disposalFees: z.array(projectDisposalFeeInputSchema).optional(),
    washoutFee: z.number().nullable().optional(),
    hseiPercent: z.number().nullable().optional(),
})

export type CreateProjectBillingTermsInput = z.infer<typeof createProjectBillingTermsInputSchema>

export function perLoadRequestFields(
    billingMethod: ProjectBillingMethod,
    ratePerLoad: number | null | undefined,
    perLoadNotes: string | null | undefined,
): {rate_per_load?: string | null; per_load_notes?: string | null} {
    if (billingMethod !== 'per_load') {
        return {}
    }

    return {
        rate_per_load: ratePerLoad != null ? ratePerLoad.toFixed(2) : null,
        per_load_notes: perLoadNotes?.trim() || null,
    }
}

export const createProjectBillingTermsRequestSchema =
    createProjectBillingTermsInputSchema.transform(
        ({
            billingMethod,
            transportationHourlyRate,
            estimatedTransportationHours,
            flatRateAmount,
            ratePerLoad,
            perLoadNotes,
            demurrageHourlyRate,
            disposalFees,
            washoutFee,
            hseiPercent,
        }) => ({
            billing_method: billingMethod,
            transportation_hourly_rate:
                transportationHourlyRate != null ? transportationHourlyRate.toFixed(2) : null,
            estimated_transportation_hours:
                estimatedTransportationHours != null
                    ? estimatedTransportationHours.toFixed(2)
                    : null,
            flat_rate_amount: flatRateAmount != null ? flatRateAmount.toFixed(2) : null,
            ...perLoadRequestFields(billingMethod, ratePerLoad, perLoadNotes),
            demurrage_hourly_rate:
                demurrageHourlyRate != null ? demurrageHourlyRate.toFixed(2) : null,
            disposal_fees: (disposalFees ?? []).map((fee) => ({
                waste_type: fee.wasteType,
                unit: fee.unit,
                unit_rate: fee.unitRate.toFixed(4),
                estimated_quantity:
                    fee.estimatedQuantity != null ? fee.estimatedQuantity.toFixed(2) : null,
            })),
            washout_fee: washoutFee != null ? washoutFee.toFixed(2) : null,
            hsei_percent: hseiPercent != null ? hseiPercent.toFixed(2) : null,
        }),
    )

export type ProjectListSortBy = 'created_at' | 'earliest_start' | 'job_count'
export type ProjectListSortDir = 'asc' | 'desc'

export type ProjectListFilters = {
    name?: string
    customerName?: string
    customerId?: string
    description?: string
    createdAfter?: string | Date | null
    createdBefore?: string | Date | null
    startDateFrom?: string | Date | null
    startDateTo?: string | Date | null
    status?: ProjectLifecycleStatus[]
    sortBy?: ProjectListSortBy
    sortDir?: ProjectListSortDir
    hasUnbilled?: boolean
    mine?: boolean
}

export const createProjectInputSchema = z.object({
    customerId: z.string(),
    name: z.string(),
    description: z.string(),
    notes: z.string().nullable().optional(),
    internalContactId: z.string(),
    customerContactId: z.string().nullable().optional(),
    poNumber: z.string().nullable().optional(),
    dispatchYardId: z.string().nullable().optional(),
    billingTerms: createProjectBillingTermsInputSchema,
    lineItems: z.lazy(() => z.array(newOrderLineItemInputSchema)).optional(),
    jobs: z.lazy(() => z.array(createProjectJobInputSchema)).optional(),
    quoteId: z.string().nullable().optional(),
})

export type CreateProjectInput = z.infer<typeof createProjectInputSchema>

const newOrderLineItemInputSchema = z.object({
    chargeType: z.string(),
    name: z.string(),
    description: z.string().optional(),
    quantity: z.number(),
    unit: z.string(),
    unitPrice: z.number(),
    totalPrice: z.number(),
})

export const createProjectRequestSchema = createProjectInputSchema.transform(
    ({
        customerId,
        name,
        description,
        notes,
        internalContactId,
        customerContactId,
        poNumber,
        dispatchYardId,
        billingTerms,
        lineItems,
        jobs,
        quoteId,
    }) => ({
        customer_id: customerId,
        name: name.trim(),
        description: description.trim(),
        notes: notes?.trim() || null,
        internal_contact_id: internalContactId,
        customer_contact_id: customerContactId ?? null,
        ...(poNumber !== undefined ? {po_number: poNumber?.trim() || null} : {}),
        ...(dispatchYardId ? {dispatch_yard_id: dispatchYardId} : {}),
        billing_terms: {
            billing_method: billingTerms.billingMethod,
            transportation_hourly_rate: billingTerms.transportationHourlyRate ?? null,
            flat_rate_amount: billingTerms.flatRateAmount ?? null,
            ...perLoadRequestFields(
                billingTerms.billingMethod,
                billingTerms.ratePerLoad,
                billingTerms.perLoadNotes,
            ),
            demurrage_hourly_rate: billingTerms.demurrageHourlyRate ?? null,
            disposal_fees: (billingTerms.disposalFees ?? []).map((fee) => ({
                waste_type: fee.wasteType,
                unit: fee.unit,
                unit_rate: fee.unitRate.toString(),
                estimated_quantity: fee.estimatedQuantity?.toString() ?? null,
            })),
            washout_fee: billingTerms.washoutFee ?? null,
            hsei_percent: billingTerms.hseiPercent ?? null,
        },
        ...(lineItems?.length
            ? {
                  line_items: lineItems.map((item) => ({
                      charge_type: item.chargeType,
                      name: item.name.trim(),
                      description: item.description?.trim() ?? '',
                      quantity: item.quantity.toFixed(2),
                      unit: item.unit,
                      unit_price: item.unitPrice.toFixed(4),
                      total_price: item.totalPrice.toFixed(2),
                  })),
              }
            : {}),
        ...(jobs?.length
            ? {jobs: jobs.map((job) => createProjectJobRequestSchema.parse(job))}
            : {}),
        ...(quoteId ? {quote_id: quoteId} : {}),
    }),
)

export const patchProjectInputSchema = z.object({
    name: z.string(),
    description: z.string(),
    notes: z.string().nullable(),
    customerId: z.string(),
    internalContactId: z.string().nullable(),
    customerContactId: z.string().nullable(),
    poNumber: z.string().nullable().optional(),
})

export type PatchProjectInput = z.infer<typeof patchProjectInputSchema>

export const patchProjectRequestSchema = patchProjectInputSchema.transform(
    ({name, description, notes, customerId, internalContactId, customerContactId, poNumber}) => ({
        name: name.trim(),
        description: description.trim(),
        notes: notes?.trim() || null,
        customer_id: customerId,
        internal_contact_id: internalContactId,
        customer_contact_id: customerContactId,
        ...(poNumber !== undefined ? {po_number: poNumber?.trim() || null} : {}),
    }),
)

const projectJobLocationRawSchema = z.object({
    id: z.string(),
    name: z.string(),
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
    notes: z.string().nullable(),
})

export const projectJobResponseRawSchema = jobScheduleRawSchema.extend({
    id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    service_type_id: z.string(),
    service_type_name: z.string(),
    notes: z.string().nullable(),
    po_number: z.string().nullable().optional(),
    project_billing_terms_id: z.string().optional(),
    status: jobStatusSchema,
    display_id: z.string().min(7).max(15),
    customer_contact: customerContactRefRawSchema.nullable().optional(),
    customer_contact_source: customerContactSourceSchema.nullable().optional(),
    locations: z.array(projectJobLocationRawSchema),
    worker_requirements: z.array(
        z.object({
            id: z.string(),
            worker_type_id: z.string(),
            quantity: z.number(),
        }),
    ),
    asset_requirements: z.array(
        z.object({
            id: z.string(),
            rental_asset_type_id: z.string(),
            quantity: z.number(),
        }),
    ),
    comments: z.array(jobCommentRawSchema),
})

export const projectJobResponseSchema = projectJobResponseRawSchema.transform((raw) => ({
    id: raw.id,
    name: raw.name,
    description: raw.description,
    serviceTypeId: raw.service_type_id,
    serviceTypeName: raw.service_type_name,
    notes: raw.notes,
    poNumber: raw.po_number ?? null,
    projectBillingTermsId: raw.project_billing_terms_id ?? null,
    status: raw.status,
    displayId: raw.display_id,
    ...mapJobSchedule(raw),
    customerContact:
        raw.customer_contact == null ? null : customerContactRefSchema.parse(raw.customer_contact),
    customerContactSource: raw.customer_contact_source ?? null,
    locations: raw.locations.map((location) => ({
        id: location.id,
        name: location.name,
        latitude: location.latitude,
        longitude: location.longitude,
        notes: location.notes,
    })),
    workerRequirements: raw.worker_requirements.map((requirement) => ({
        id: requirement.id,
        workerTypeId: requirement.worker_type_id,
        quantity: requirement.quantity,
    })),
    assetRequirements: raw.asset_requirements.map((requirement) => ({
        id: requirement.id,
        rentalAssetTypeId: requirement.rental_asset_type_id,
        quantity: requirement.quantity,
    })),
    comments: raw.comments.map((comment) => ({
        id: comment.id,
        content: comment.content,
        createdAt: comment.created_at,
        createdById: comment.created_by_id,
        createdByName: comment.created_by_name,
        updatedAt: comment.updated_at,
        updatedById: comment.updated_by_id,
        updatedByName: comment.updated_by_name,
    })),
}))

export type ProjectJobResponse = z.infer<typeof projectJobResponseSchema>

const requirementBillingTypeSchema = z.enum([
    'hourly',
    'daily',
    'weekly',
    'monthly',
    'flat',
    'per_load',
])

function requirementBillingRequest(requirement: {
    billingRate?: string | null
    billingType?: string | null
}) {
    return requirement.billingRate && requirement.billingType
        ? {billing_rate: requirement.billingRate, billing_type: requirement.billingType}
        : {}
}

export const createProjectJobInputSchema = z.object({
    status: z.enum(['planned', 'active']).optional(),
    name: z.string(),
    serviceTypeId: z.string(),
    notes: z.string().nullable().optional(),
    customerContactId: z.string().nullable().optional(),
    poNumber: z.string().nullable().optional(),
    projectBillingTermsId: z.string().nullable().optional(),
    startDate: z.string(),
    startTime: z.string().nullable().optional(),
    endDate: z.string().nullable().optional(),
    endTime: z.string().nullable().optional(),
    dateTbc: z.boolean().optional(),
    bookByDay: z.boolean().optional(),
    specificType: z.string().nullable().optional(),
    plannedEndDate: z.string().nullable().optional(),
    locations: z.array(locationInputSchema),
    workerRequirements: z
        .array(
            z.object({
                workerTypeId: z.string(),
                quantity: z.number(),
                billingRate: z.string().nullable().optional(),
                billingType: requirementBillingTypeSchema.nullable().optional(),
                estimatedHours: z.number().nullable().optional(),
            }),
        )
        .optional(),
    assetRequirements: z
        .array(
            z.object({
                rentalAssetTypeId: z.string(),
                quantity: z.number(),
                billingRate: z.string().nullable().optional(),
                billingType: requirementBillingTypeSchema.nullable().optional(),
            }),
        )
        .optional(),
})

export type CreateProjectJobInput = z.infer<typeof createProjectJobInputSchema>

export const createProjectJobRequestSchema = createProjectJobInputSchema.transform(
    ({
        name,
        status,
        serviceTypeId,
        notes,
        customerContactId,
        poNumber,
        projectBillingTermsId,
        startDate,
        startTime,
        endDate,
        endTime,
        dateTbc,
        bookByDay,
        specificType,
        plannedEndDate,
        locations,
        workerRequirements,
        assetRequirements,
    }) => ({
        name: name.trim(),
        ...(status !== undefined ? {status} : {}),
        service_type_id: serviceTypeId,
        notes: notes?.trim() || null,
        customer_contact_id: customerContactId ?? null,
        ...(poNumber !== undefined ? {po_number: poNumber?.trim() || null} : {}),
        ...(projectBillingTermsId !== undefined
            ? {project_billing_terms_id: projectBillingTermsId}
            : {}),
        start_date: startDate,
        start_time: startTime ?? null,
        end_date: endDate ?? null,
        end_time: endTime ?? null,
        ...(dateTbc ? {date_tbc: true} : {}),
        ...(bookByDay ? {book_by_day: true} : {}),
        ...(specificType?.trim() ? {specific_type: specificType.trim()} : {}),
        ...(plannedEndDate ? {planned_end_date: plannedEndDate} : {}),
        locations: locations.map(toLocationRequest),
        worker_requirements: (workerRequirements ?? []).map((requirement) => ({
            worker_type_id: requirement.workerTypeId,
            quantity: requirement.quantity,
            ...requirementBillingRequest(requirement),
            ...(requirement.estimatedHours != null
                ? {estimated_hours: String(requirement.estimatedHours)}
                : {}),
        })),
        asset_requirements: (assetRequirements ?? []).map((requirement) => ({
            rental_asset_type_id: requirement.rentalAssetTypeId,
            quantity: requirement.quantity,
            ...requirementBillingRequest(equipmentBilling(requirement)),
        })),
    }),
)

export const createProjectBillableLineItemInputSchema = z.object({
    jobId: z.string().nullable().optional(),
    chargeType: z.string(),
    name: z.string(),
    description: z.string().optional(),
    quantity: z.number(),
    unit: z.string(),
    unitPrice: z.number(),
    totalPrice: z.number(),
    poNumber: z.string().nullable().optional(),
    projectBillingTermsId: z.string().nullable().optional(),
})

export type CreateProjectBillableLineItemInput = z.infer<
    typeof createProjectBillableLineItemInputSchema
>

export const createProjectBillableLineItemRequestSchema =
    createProjectBillableLineItemInputSchema.transform(
        ({
            jobId,
            chargeType,
            name,
            description,
            quantity,
            unit,
            unitPrice,
            totalPrice,
            poNumber,
            projectBillingTermsId,
        }) => ({
            job_id: jobId ?? null,
            charge_type: chargeType,
            name: name.trim(),
            description: description?.trim() ?? '',
            quantity: quantity.toFixed(2),
            unit,
            unit_price: unitPrice.toFixed(2),
            total_price: totalPrice.toFixed(2),
            ...(poNumber !== undefined ? {po_number: poNumber?.trim() || null} : {}),
            ...(projectBillingTermsId !== undefined
                ? {project_billing_terms_id: projectBillingTermsId}
                : {}),
        }),
    )
